import React from 'react';
import { useTranslation } from 'react-i18next';
import { object } from 'yup';
import {
  pastYear,
  string,
} from '../../../../../../common/src/constants/validationSchemas';

// Components
import ActionButtons from '../annexes/ActionButtons';

// Types
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  VStack,
} from '@wegroup/design-system';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { PiaClaim } from './PiaClaimsInputComponent';
import {
  CAUSES_OPTIONS,
  LOSS_AMOUNT_OPTIONS,
} from '../../../../../../tarifications/src/legacy/ProFlowPiaClaims';

interface Props {
  accident?: PiaClaim;
  isMobile?: boolean;
  handleSave: (arg0: PiaClaim) => void;
  handleRemove?: () => void;
  handleCancel: () => void;
}

const AccidentForm: React.FC<React.PropsWithChildren<Props>> = ({
  isMobile,
  accident,
  handleSave,
  handleRemove,
  handleCancel,
}) => {
  const { t } = useTranslation();

  const schema = object().shape({
    cause: string.required,
    loss_amount: string.required,
    year: pastYear.required,
  });

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<PiaClaim>({
    resolver: yupResolver(schema),
    defaultValues: {
      cause: accident?.cause || '',
      loss_amount: accident?.loss_amount || '',
      year: accident?.year || '',
    },
  });

  const onSubmit = (values: PiaClaim) => {
    handleSave(values);
  };

  return (
    <Box bg={{ base: 'white', laptop: 'unset' }} borderRadius="6px">
      <VStack
        spacing="2"
        align="center"
        bg="white"
        borderRadius="md"
        p="3"
        boxShadow="0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
        0 0 1px rgba(0, 0, 0, 0.04)"
      >
        <FormControl isInvalid={!!errors.cause}>
          <FormLabel>{t('Claim cause')}</FormLabel>
          <Select {...register('cause')} placeholder={t('Choose an option')}>
            {CAUSES_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </Select>

          <FormErrorMessage mt="0.5">{errors.cause?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.loss_amount}>
          <FormLabel>{t('Loss amount')}</FormLabel>
          <Select
            {...register('loss_amount')}
            placeholder={t('Choose an option')}
          >
            {LOSS_AMOUNT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </Select>
          <FormErrorMessage mt="0.5">
            {errors.loss_amount?.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.year}>
          <FormLabel>{t('What year did it occur')}</FormLabel>
          <Input {...register('year')} placeholder="YYYY" type="number" />
          <FormErrorMessage mt="0.5">{errors.year?.message}</FormErrorMessage>
        </FormControl>
      </VStack>
      <ActionButtons
        handleSubmit={handleSubmit(onSubmit)}
        handleRemove={handleRemove}
        handleCancel={handleCancel}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default AccidentForm;
